



































































































































































































































































































































































































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, isEqual } from 'lodash';
import moment from 'moment';
import { AxiosError } from 'axios';

import {
  IRequisition,
  Requisition,
  RequisitionLine,
  RequisitionFieldError,
  Vendor,
  RequisitionSupplierAddress,
  IRequisitionSupplierAddressData,
  IRequisitionSupplierAddress,
  IRequisitionSupplierContactData,
  RequisitionSupplierContact,
  IRequisitionSupplierContact,
  RequisitionSupplier,
  IRequisitionAttachment,
  Currency,
  Country
} from '@/models';

import { ALForm } from '@/common/component.types';

import { ErrorManager } from '@/models/error';
import { diffDeep, scrollToBottomOfHtmlElement } from '@/utils/helpers';

import RequisitionDetailFormLineCard from '@/components/requisition/RequisitionDetailFormLineCard.vue';
import VendorSearch from '@/components/vendor/VendorSearch.vue';
import CountryPicker from '../common/CountryPicker.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import ALDialog from '../core/Dialog/ALDialog.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import RequisitionStatusDisplay from '../requisition-common/RequisitionStatusDisplay.vue';

export interface Options extends InstanceType<typeof Vue> {
  $refs: {
    form: ALForm;
  };
}

export default Vue.extend<Options>({
  functional: false
}).extend({
  name: 'RequisitionDetailFormDialog',

  components: {
    RequisitionDetailFormLineCard,
    VendorSearch,
    CountryPicker,
    ConfirmationDialog,
    ErrorAlert,
    ALDialog,
    DragDropFileInput,
    RequisitionStatusDisplay
  },

  data: () => ({
    requisitionFormValid: true,
    loading: false,
    isEdit: false,
    dialog: false,
    currencyLoading: false,
    departmentLoading: false,
    glAccountsLoading: false,
    dateMenu: false,
    requisitionOriginalCopy: null as null | IRequisition,
    requisition: null as null | IRequisition,
    fieldError: new RequisitionFieldError(),
    isDragOver: false,

    errorMessage: '',
    errorMessageDetail: '',
    confirmDialog: false
  }),

  mounted() {
    // fetch all currencies
    this.currencyLoading = true;
    this.fetchCurrencies()
      .catch(error => {
        this.errorMessage = ErrorManager.extractApiError(error);
        this.fieldError.currency.push('Error occurred getting currencies');
      })
      .finally(() => (this.currencyLoading = false));

    // fetch all departments
    this.departmentLoading = true;
    this.fetchDepartments()
      .catch(error => {
        this.errorMessage = ErrorManager.extractApiError(error);
      })
      .finally(() => (this.departmentLoading = false));

    // fetch all GL Accounts
    this.glAccountsLoading = true;
    this.fetchGLAccounts()
      .catch(error => {
        this.errorMessage = ErrorManager.extractApiError(error);
      })
      .finally(() => (this.glAccountsLoading = false));
  },

  computed: {
    ...mapGetters({
      accounts: 'accounting/getGLAccounts',
      currencies: 'currencyNew/getCurrencies',
      getCurrencyByCode: 'currencyNew/getCurrencyByCode',
      departments: 'department/getDepartments',
      lastCreatedRequisitionId: 'requisition/getLastCreatedRequisitionId'
    }),

    totalAmount(): number {
      const sum = this.requisition!.lines.reduce(function(a, s) {
        return (a += s.unit_price * s.quantity);
      }, 0);

      return this.$options.filters!.currency(sum);
    },

    errorOccurred(): boolean {
      return this.errorMessage.length > 0;
    }
  },

  methods: {
    ...mapActions({
      fetchGLAccounts: 'accounting/fetchGLAccounts',
      fetchCurrencies: 'currencyNew/fetchCurrencies',
      fetchDepartments: 'department/fetchDepartments',
      createRequisition: 'requisition/createRequisition',
      updateRequisition: 'requisition/updateRequisition',
      deleteRequisitionAttachment: 'requisition/deleteRequisitionAttachment'
    }),

    resetFormValidation(): void {
      if (this.$refs.form) {
        return this.$refs.form.resetValidation();
      }
    },

    validateForm(): boolean {
      let valid = false;
      if (this.$refs.form) {
        valid = this.$refs.form.validate();
      }
      return valid;
    },

    open(requisition: IRequisition = new Requisition(), isEdit = false) {
      this.requisitionOriginalCopy = requisition;
      this.requisition = cloneDeep(this.requisitionOriginalCopy);
      this.isEdit = isEdit;
      this.clearAllErrors();
      this.dialog = true;
      this.resetFormValidation();
    },

    close() {
      if (
        !isEqual(this.requisition, this.requisitionOriginalCopy) &&
        this.isEdit
      ) {
        this.confirmDialog = true;
      } else {
        this.closeDialog();
      }
    },

    formatDate(date: string): string {
      return date ? moment(date).format('MMMM D YYYY') : '';
    },

    allowedDates: (val: string) => {
      return moment(val).isSameOrAfter(new Date(), 'day');
    },

    clearRequitionSupplierAddress() {
      this.requisition!.supplier!.address = new RequisitionSupplierAddress();
    },

    setRequitionSupplierAddress(vendor: Vendor): void {
      if (vendor) {
        this.clearAllErrors();

        const vendorSupplierContactData: IRequisitionSupplierContactData = {
          name: vendor.contact,
          email: vendor?.email,
          phone: vendor?.phone
        };

        const vendorSupplierAddressData: IRequisitionSupplierAddressData = {
          address1: vendor.address1,
          address2: vendor.address2,
          city: vendor.city,
          region: vendor.region,
          postal_code: vendor.postal_code,
          country: (vendor.country as Country).code
        };

        const supplierAddress: IRequisitionSupplierAddress = new RequisitionSupplierAddress(
          vendorSupplierAddressData
        );
        const supplierContact: IRequisitionSupplierContact = new RequisitionSupplierContact(
          vendorSupplierContactData
        );

        const supplier = new RequisitionSupplier({
          name: vendor.name,
          vendor_code: vendor.id,
          address: supplierAddress ?? this.requisition?.supplier?.address,
          contact: supplierContact ?? new RequisitionSupplierContact()
        });

        this.requisition!.supplier = supplier;

        const currency: Currency = this.getCurrencyByCode(vendor.currency);
        if (currency) {
          this.requisition!.currency = currency.id;
        }
      }
    },

    async addRequisitionAttachments(file: File | File[]) {
      this.clearAllErrors();

      const fileList = file && Array.isArray(file) ? file : [file];

      fileList.forEach(file => {
        this.requisition?.attachFile(file);
      });
      this.$nextTick(() => {
        scrollToBottomOfHtmlElement('#scrollableAttachmentList');
      });
    },

    async removeRequisitionAttachment(attachment: IRequisitionAttachment) {
      this.clearAllErrors();
      if (attachment.id) {
        await this.deleteRequisitionAttachment({
          requisitionId: this.requisition?.id,
          attachmentId: attachment.id
        }).catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        });
      }
      this.requisition?.detachFile(attachment.file as File);
    },

    addLine(): void {
      this.requisition!.addLine(new RequisitionLine());
    },

    deleteLine(line: number): void {
      const index = line - 1;
      this.requisition!.removeLine(index);
    },

    clearAllErrors(): void {
      this.fieldError.clearErrors();
      this.errorMessage = '';
      this.errorMessageDetail = '';
    },

    clearSupplierContactErrors(): void {
      if (this.fieldError.supplier && this.fieldError.supplier.contact) {
        this.fieldError.supplier.contact.clearErrors();
      }
    },

    closeDialog() {
      this.closeConfirmDialog();
      this.dialog = false;
    },
    closeConfirmDialog() {
      this.confirmDialog = false;
    },

    async saveRequisition(): Promise<void> {
      if (this.validateForm()) {
        this.confirmDialog = false;

        this.clearAllErrors();

        this.loading = true;

        try {
          if (this.isEdit) {
            const requisitionDelta = diffDeep(
              this.requisition,
              this.requisitionOriginalCopy,
              true
            );
            requisitionDelta!.id = this.requisition!.id;
            await this.updateRequisition(requisitionDelta);
          } else {
            await this.createRequisition(this.requisition);
            this.$emit('created:requisition', this.lastCreatedRequisitionId);
          }

          // emit success
          this.$emit('success');
          // bypass close dialog checks
          this.dialog = false;
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (error.response.data.detail) {
              this.errorMessage = error.response.data.detail;
            }
            // client received an error response that falls out of range 2xx
            if (
              RequisitionFieldError.isRequisitionFieldError(error.response.data)
            ) {
              this.fieldError = new RequisitionFieldError(error.response.data);
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      }
    }
  }
});
